/**
 * Connector orchestrator. Turns chassis connector payloads into Wasiyat
 * vault mutations. The connectors themselves only know their domain; this
 * file is the bridge that knows where each piece of data goes.
 *
 * Hard rules:
 *  - Never write raw secrets to the vault (Aadhaar full number, share text)
 *  - Always set sensible defaults so a fast "pull all" creates usable rows
 *  - Always preserve user-entered data — merge, do not clobber
 *  - Mutations go through store.mutate() so persist + readiness recompute fire
 */

import { store } from '../store.ts';
import { newId, toPaise } from '../types.ts';
import type {
  Asset,
  AssetCategory,
  AssetId,
  DocumentCategory,
  DocumentEntry,
  DocumentId,
  Nominee,
  NomineeId,
} from '../types.ts';

import type { DigilockerDoc, DigilockerSnapshot } from './connectors/govt/digilocker.ts';
import { DIGILOCKER_TO_WASIYAT } from './connectors/govt/digilocker.ts';
import type { AadhaarOfflineKyc } from './connectors/govt/aadhaar-offline-kyc.ts';
import type { MparivahanSnapshot } from './connectors/govt/mparivahan.ts';
import type { ItrSnapshot, ItrScheduleAlRow } from './connectors/govt/itr-importer.ts';
import type { PassportInfo } from './connectors/govt/passport-parser.ts';
import type { CowinCertificate } from './connectors/govt/cowin-vax-cert.ts';
import type { PropertyAsset } from './connectors/govt/sro-property.ts';
import type { IrctcSnapshot } from './connectors/govt/irctc-history.ts';

export interface OrchestratorReport {
  readonly source: string;
  readonly assetsCreated: number;
  readonly documentsCreated: number;
  readonly nomineesTouched: number;
  readonly skipped: readonly string[];
}

const empty: Omit<OrchestratorReport, 'source'> = Object.freeze({
  assetsCreated: 0,
  documentsCreated: 0,
  nomineesTouched: 0,
  skipped: Object.freeze([]) as readonly string[],
});

const nowIso = (): string => new Date().toISOString();

// ──────────────────── DigiLocker ────────────────────

export async function ingestDigilocker(snap: DigilockerSnapshot): Promise<OrchestratorReport> {
  let assetsCreated = 0;
  let documentsCreated = 0;
  const skipped: string[] = [];

  await store.mutate((draft) => {
    for (const doc of snap.docs) {
      const mapping = DIGILOCKER_TO_WASIYAT[doc.type];
      if (alreadyHasDocument(draft.documents, doc)) {
        skipped.push(doc.title);
        continue;
      }
      const entry: DocumentEntry = {
        id: newId<DocumentId>(),
        category: mapping.documentCategory as DocumentCategory,
        title: doc.title,
        blob_id: null,
        size_kb: Math.round(doc.sizeBytes / 1024),
        mime_type: doc.mimeType,
        notes: `via DigiLocker · ref ${doc.uriRefId}`,
        password_protected: false,
        created_at: nowIso(),
      };
      draft.documents.push(entry);
      documentsCreated++;

      if (mapping.suggestedAssetCategory) {
        const assetCat = mapping.suggestedAssetCategory as AssetCategory;
        if (!alreadyHasAsset(draft.assets, assetCat, doc.uriRefId)) {
          const asset: Asset = {
            id: newId<AssetId>(),
            category: assetCat,
            institution: doc.issuer,
            identifier_last4: doc.uriRefId.slice(-4),
            estimated_value_paise: 0,
            custodian_notes: `auto-imported from DigiLocker · ${doc.title}`,
            nominee_ids: [],
            document_ids: [entry.id],
            created_at: nowIso(),
            updated_at: nowIso(),
          };
          draft.assets.push(asset);
          assetsCreated++;
        }
      }
    }
  });

  return { source: 'digilocker', assetsCreated, documentsCreated, nomineesTouched: 0, skipped };
}

// ──────────────────── Aadhaar offline e-KYC ─────────

export async function ingestAadhaar(payload: AadhaarOfflineKyc): Promise<OrchestratorReport> {
  let nomineesTouched = 0;
  let documentsCreated = 0;
  const skipped: string[] = [];

  await store.mutate((draft) => {
    const haveDoc = draft.documents.some((d) => d.category === 'aadhaar');
    if (!haveDoc) {
      const entry: DocumentEntry = {
        id: newId<DocumentId>(),
        category: 'aadhaar',
        title: `Aadhaar e-KYC · ${payload.name}`,
        blob_id: null,
        size_kb: 0,
        mime_type: 'application/xml',
        notes: `offline e-KYC · sig ${payload.signatureValid ? 'verified' : 'unverified'} · last4 ${payload.aadhaarLast4}`,
        password_protected: false,
        created_at: nowIso(),
      };
      draft.documents.push(entry);
      documentsCreated++;
    } else {
      skipped.push('aadhaar-doc-exists');
    }
    // Bind the principal Aadhaar last4 to the holder's own nominee slot if
    // present (we treat the vault owner as nominee index 0 by convention if
    // they declared themselves).
    for (const n of draft.nominees) {
      if (n.display_name.toLowerCase().startsWith(payload.name.split(' ')[0]?.toLowerCase() ?? '__no__match__')) {
        if (n.aadhaar_last4 === payload.aadhaarLast4) continue;
        n.aadhaar_last4 = payload.aadhaarLast4;
        nomineesTouched++;
        break;
      }
    }
  });

  return { source: 'aadhaar-offline-kyc', assetsCreated: 0, documentsCreated, nomineesTouched, skipped };
}

// ──────────────────── mParivahan ────────────────────

export async function ingestMparivahan(snap: MparivahanSnapshot): Promise<OrchestratorReport> {
  let assetsCreated = 0;
  let documentsCreated = 0;
  const skipped: string[] = [];

  await store.mutate((draft) => {
    for (const v of snap.vehicles) {
      if (alreadyHasAssetByInstitution(draft.assets, 'insurance_motor', v.registrationNo)) {
        skipped.push(`vehicle-${v.registrationNo}`);
        continue;
      }
      const insuranceAsset: Asset = {
        id: newId<AssetId>(),
        category: 'insurance_motor',
        institution: `Vehicle insurance · ${v.make} ${v.model}`,
        identifier_last4: v.registrationNo.slice(-4),
        estimated_value_paise: 0,
        custodian_notes: `RC ${v.registrationNo} · insured till ${v.insuranceUpto} · fitness ${v.fitnessUpto}`,
        nominee_ids: [],
        document_ids: [],
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      draft.assets.push(insuranceAsset);
      assetsCreated++;
      // Also create a placeholder document for the RC itself.
      draft.documents.push({
        id: newId<DocumentId>(),
        category: 'other',
        title: `RC · ${v.registrationNo} · ${v.make} ${v.model}`,
        blob_id: null,
        size_kb: 0,
        mime_type: 'application/pdf',
        notes: `mParivahan import · RTO ${v.rtoCode} · ${v.state}`,
        password_protected: false,
        created_at: nowIso(),
      });
      documentsCreated++;
    }
  });

  return { source: 'mparivahan', assetsCreated, documentsCreated, nomineesTouched: 0, skipped };
}

// ──────────────────── ITR Schedule AL ───────────────

export async function ingestItr(snap: ItrSnapshot): Promise<OrchestratorReport> {
  let assetsCreated = 0;
  const skipped: string[] = [];

  await store.mutate((draft) => {
    for (const row of snap.scheduleAl) {
      const cat = mapScheduleAlCategory(row);
      const fingerprint = `${cat}::${row.description.toLowerCase()}`;
      if (alreadyHasFingerprint(draft.assets, fingerprint)) {
        skipped.push(`itr::${row.description}`);
        continue;
      }
      const asset: Asset = {
        id: newId<AssetId>(),
        category: cat,
        institution: row.description.length > 60 ? `${row.description.slice(0, 60)}…` : row.description,
        identifier_last4: '----',
        estimated_value_paise: row.costPaise,
        custodian_notes: `Schedule AL · ITR ${snap.identity.formCode} · AY ${snap.identity.assessmentYear}`,
        nominee_ids: [],
        document_ids: [],
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      draft.assets.push(asset);
      assetsCreated++;
    }
  });

  return { source: 'itr-importer', assetsCreated, documentsCreated: 0, nomineesTouched: 0, skipped };
}

function mapScheduleAlCategory(row: ItrScheduleAlRow): AssetCategory {
  switch (row.category) {
    case 'immovable': return 'property';
    case 'bank': return 'bank_account';
    case 'shares': return 'demat';
    case 'insurance': return 'insurance_life';
    case 'jewellery': return 'jewellery';
    case 'vehicle': return 'insurance_motor';
    case 'cash': return 'other';
    case 'movable': return 'other';
    default: return 'other';
  }
}

// ──────────────────── Passport ──────────────────────

export async function ingestPassport(info: PassportInfo): Promise<OrchestratorReport> {
  let documentsCreated = 0;
  const skipped: string[] = [];

  await store.mutate((draft) => {
    if (draft.documents.some((d) => d.category === 'passport')) {
      skipped.push('passport-exists');
      return;
    }
    draft.documents.push({
      id: newId<DocumentId>(),
      category: 'passport',
      title: `Passport · ${info.surname} ${info.givenNames}`,
      blob_id: null,
      size_kb: 0,
      mime_type: 'application/pdf',
      notes: `${info.issuingCountry} · expires ${info.expiryDate} · MRZ ${info.compositeValid ? 'verified' : 'flagged'}`,
      password_protected: false,
      created_at: nowIso(),
    });
    documentsCreated++;
  });

  return { source: 'passport-parser', assetsCreated: 0, documentsCreated, nomineesTouched: 0, skipped };
}

// ──────────────────── CoWIN ─────────────────────────

export async function ingestCowin(cert: CowinCertificate): Promise<OrchestratorReport> {
  let documentsCreated = 0;
  const skipped: string[] = [];

  await store.mutate((draft) => {
    const haveDose = (n: number): boolean =>
      draft.documents.some((d) => d.category === 'medical_record' && d.title.includes(`CoWIN dose ${n}`));
    for (const d of cert.doses) {
      if (haveDose(d.doseNumber)) {
        skipped.push(`dose-${d.doseNumber}`);
        continue;
      }
      draft.documents.push({
        id: newId<DocumentId>(),
        category: 'medical_record',
        title: `CoWIN dose ${d.doseNumber} · ${d.vaccineName}`,
        blob_id: null,
        size_kb: 0,
        mime_type: 'application/pdf',
        notes: `${d.dateOfDose} · batch ${d.batchNumber} · ${d.siteName}`,
        password_protected: false,
        created_at: nowIso(),
      });
      documentsCreated++;
    }
  });

  return { source: 'cowin-vax-cert', assetsCreated: 0, documentsCreated, nomineesTouched: 0, skipped };
}

// ──────────────────── SRO property ──────────────────

export async function ingestSroProperty(rows: readonly PropertyAsset[]): Promise<OrchestratorReport> {
  let assetsCreated = 0;
  let documentsCreated = 0;
  const skipped: string[] = [];

  await store.mutate((draft) => {
    for (const p of rows) {
      if (alreadyHasFingerprint(draft.assets, `property::${p.registrationNo}`)) {
        skipped.push(`property-${p.registrationNo}`);
        continue;
      }
      const asset: Asset = {
        id: newId<AssetId>(),
        category: 'property',
        institution: `${p.subRegistrarOffice || p.state} · ${p.registrationNo}`,
        identifier_last4: p.registrationNo.slice(-4),
        estimated_value_paise: p.considerationAmountPaise,
        custodian_notes: `${p.propertyAddress.slice(0, 90)} · registered ${p.registeredOn}`,
        nominee_ids: [],
        document_ids: [],
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      draft.assets.push(asset);
      assetsCreated++;

      draft.documents.push({
        id: newId<DocumentId>(),
        category: 'property_deed',
        title: `Sale deed · ${p.registrationNo}`,
        blob_id: null,
        size_kb: 0,
        mime_type: 'application/pdf',
        notes: `${p.buyerNamePrimary} ← ${p.sellerNamePrimary} · ${p.state}`,
        password_protected: true,
        created_at: nowIso(),
      });
      documentsCreated++;
    }
  });

  return { source: 'sro-property', assetsCreated, documentsCreated, nomineesTouched: 0, skipped };
}

// ──────────────────── IRCTC (lightweight) ───────────

export async function ingestIrctc(snap: IrctcSnapshot): Promise<OrchestratorReport> {
  // Travel history is context, not a vault entry. We surface a single
  // "travel history" placeholder document so the family can find it.
  const skipped: string[] = [];
  let documentsCreated = 0;
  if (snap.bookings.length === 0) {
    skipped.push('no-bookings');
    return { source: 'irctc-history', ...empty, skipped };
  }
  await store.mutate((draft) => {
    if (draft.documents.some((d) => d.title === 'IRCTC travel history')) {
      skipped.push('exists');
      return;
    }
    draft.documents.push({
      id: newId<DocumentId>(),
      category: 'other',
      title: 'IRCTC travel history',
      blob_id: null,
      size_kb: 0,
      mime_type: 'text/csv',
      notes: `${snap.bookings.length} bookings imported`,
      password_protected: false,
      created_at: nowIso(),
    });
    documentsCreated++;
  });
  return { source: 'irctc-history', assetsCreated: 0, documentsCreated, nomineesTouched: 0, skipped };
}

// ──────────────────── helpers ───────────────────────

function alreadyHasDocument(docs: readonly DocumentEntry[], dl: DigilockerDoc): boolean {
  return docs.some((d) => d.notes.includes(dl.uriRefId));
}

function alreadyHasAsset(assets: readonly Asset[], cat: AssetCategory, refId: string): boolean {
  const tail = refId.slice(-4);
  return assets.some((a) => a.category === cat && a.identifier_last4 === tail);
}

function alreadyHasAssetByInstitution(assets: readonly Asset[], cat: AssetCategory, registration: string): boolean {
  return assets.some((a) => a.category === cat && a.custodian_notes.includes(registration));
}

function alreadyHasFingerprint(assets: readonly Asset[], fingerprint: string): boolean {
  return assets.some((a) =>
    `${a.category}::${a.institution.toLowerCase()}`.includes(fingerprint.split('::')[1] ?? '')
  );
}

// ──────────────────── nominee suggestions ───────────

export function suggestNomineesFromDigilocker(_snap: DigilockerSnapshot): Partial<Nominee>[] {
  // DigiLocker's family-link list is not part of the public issued-docs
  // surface. Until that lands we return empty and let the user add nominees
  // manually. Hook kept here for v1.1.
  return [];
}

// Pure unit-test surface — used by smoke_test.
export function mapScheduleAlForTests(row: ItrScheduleAlRow): AssetCategory {
  return mapScheduleAlCategory(row);
}

export const _testTypes = { nomineeId: null as NomineeId | null };
